

if (!window.customElements.get('product-engraving-modal')) {
  class ProductEngravingModal extends ModalDialog {
    constructor() {
      super();
      this.errorEl = this.querySelector('#engraving-error');
      this.engravingActive = false;
      this.engravingText = '';
      this.engravingFont = '';
      
      // Store checkbox state
      this.termsAccepted = false;
      
      // Track modal click state to prevent unintended closing
      this.mouseDownOutside = false;
      
      this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
      this.addEventListener('keyup', (event) => {
        if (!event.code) return;
        if (event.code.toUpperCase() === 'ESCAPE') this.hide();
      });
      
      // Fix mouseup/mousedown tracking to prevent modal closing when text selection extends outside
      if (this.classList.contains('media-modal')) {
        // No longer using pointerup event for closing
        this.removeHandlers();
      } else {
        // Replace the click handler with proper mousedown/mouseup tracking
        this.removeHandlers();
      }
      
      // Add mousedown listener to track where the interaction starts
      document.addEventListener('mousedown', this.handleMouseDown.bind(this));
      
      // Add mouseup listener to potentially close the modal
      document.addEventListener('mouseup', this.handleMouseUp.bind(this));

      this.querySelectorAll('[data-icon]').forEach((button) => {
        button.addEventListener('click', () => {
          const textarea = this.querySelector('.engraving-text');
          const currentText = textarea.value;
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          const icon = button.getAttribute('data-icon');
          
          // Only add the icon if it won't exceed the 15 character limit
          if ([...currentText].length < 15) {
            // Insert the icon at the cursor position
            textarea.value = currentText.substring(0, start) + icon + currentText.substring(end);
            textarea.focus();
            // Move the cursor after the inserted icon (treat as 1 character)
            textarea.selectionStart = textarea.selectionEnd = start + 1;
          }
          
          // Update character counter and ensure 15 char limit
          this.updateCharacterCount(textarea);
          this.updateApplyButtonState();
        });
      });

      const textarea = this.querySelector('.engraving-text');
      const termsCheckbox = this.querySelector('#engraving_terms');
      
      textarea.addEventListener('input', (event) => {
        this.updateCharacterCount(event.target);
        this.updateApplyButtonState();
      });

      // Preserve checkbox state
      termsCheckbox.checked = this.termsAccepted;
      termsCheckbox.addEventListener('change', (e) => {
        this.termsAccepted = e.target.checked;
        this.updateApplyButtonState();
      });

      this.querySelector('.cancel').addEventListener('click', (evt) => {
        this.hide();
        // clear the input
        this.querySelector('.engraving-text').value = '';
        // Note: Don't reset the checkbox state, keep it as per request
        // Reset the button UI if no active engraving
        if (!this.engravingActive) {
          this.updateEngravingStatus(false);
        }
      });

      this.querySelector('.apply').addEventListener('click', (evt) => {
        const textarea = this.querySelector('.engraving-text');
        const terms = this.querySelector('#engraving_terms');
        
        // If no text, treat as removing engraving
        if (!textarea.value) {
          // Reset engraving state
          this.engravingActive = false;
          this.engravingText = '';
          this.engravingFont = '';
          this.selectedFontText = '';
          
          // Update UI to reflect removed engraving
          this.updateEngravingStatus(false);
          this.hide();
          return;
        }
        
        // Only require checkbox when there's a message
        if (textarea.value && !terms.checked) {
          this.errorEl.textContent = 'Please accept the terms';
          return;
        }
        
        setTimeout(() => {
          this.errorEl.textContent = '';
        }, 3000);
        
        // Store engraving details for the product
        const fontSelect = this.querySelector('#engraving_font');
        const selectedFont = fontSelect.options[fontSelect.selectedIndex].text;
        
        // Decode any HTML entities in the text (including & becoming &amp;)
        const engravingText = this.decodeHTMLEntities(textarea.value);
        const engravingProductId = textarea.getAttribute('data-product');
        
        // Save to instance properties
        this.engravingActive = true;
        this.engravingText = engravingText;
        this.engravingFont = fontSelect.value;
        this.selectedFontText = selectedFont;
        this.engravingProductId = engravingProductId;
        
        // Update the button UI
        this.updateEngravingStatus(true, selectedFont, engravingText);
        
        this.hide();
        
        // Set up event listener for the Add to Cart button if not already done
        this.setupAddToCartListener();
      });
      
      // Initialize the UI
      this.initializeUI();
      
      // Set up listener for Add to Cart button
      this.setupAddToCartListener();
    }
    
    // Helper method to decode HTML entities
    decodeHTMLEntities(text) {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    }
    
    // Update character count and enforce 15 character limit
    updateCharacterCount(textarea) {
      const counter = this.querySelector('.characters-counter');
      const currentText = textarea.value;
      
      // Get proper character count using spread operator to handle Unicode properly
      const displayLength = [...currentText].length;
      
      // Enforce the 15 character limit
      if (displayLength > 15) {
        // Trim to first 15 characters (including proper handling of Unicode)
        textarea.value = [...currentText].slice(0, 15).join('');
      }
      
      // Update the counter with current length
      counter.textContent = [...textarea.value].length;
    }
    
    // Set up the event listener for the Add to Cart button
    setupAddToCartListener() {
      // Only set up the listener once
      if (this.addToCartListenerSetup) return;
      
      const addToCartButton = document.getElementById('purchaseComplete');
      if (!addToCartButton) return;
      
      // Add event listener to the form containing the Add to Cart button
      const form = addToCartButton.closest('form');
      if (form) {
        form.addEventListener('submit', this.handleAddToCart.bind(this));
        this.addToCartListenerSetup = true;
      }
    }
    
    // Handle the Add to Cart form submission
    handleAddToCart(event) {
      // Prevent the default form submission
      event.preventDefault();
      
      // Get the form data
      const formData = new FormData(event.target);
      const variantId = formData.get('id');
      
      // Get all properties from the form
      const properties = {};
      for (const [key, value] of formData.entries()) {
        // Check if this is a property field
        if (key.includes('properties[')) {
          // Extract the property name by removing 'properties[' and the trailing ']'
          const propName = key.replace('properties[', '').replace(']', '');
          properties[propName] = value;
        }
      }

      // Debug log
      console.log('Form entries:', Array.from(formData.entries()));
      console.log('Extracted properties:', properties);
      
      // If engraving is not active, just add the main product
      if (!this.engravingActive || !this.engravingText || !this.engravingProductId) {
        const data = {
          items: [{
            id: variantId,
            quantity: formData.get('quantity') || 1,
            properties: properties
          }]
        };
        
        // Add product to cart
        fetch(window.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        })
        .then(response => response.json())
        .then(result => {
          // Redirect to cart if that's the cart action setting
          const addToCartButton = document.getElementById('purchaseComplete');
          if (addToCartButton && addToCartButton.dataset.cartAction === 'page') {
            window.location.href = window.routes.cart_url;
          } else {
            // Default to redirecting to cart
            window.location.href = window.routes.cart_url;
          }
        })
        .catch(error => {
          console.error('Error in cart process:', error);
          // If there's an error, try to submit the form normally
          event.target.submit();
        });
        return;
      }
      
      // If engraving is active, add both products
      const engravingProperties = {
        'Engraving text': this.engravingText,
        'Engraving font': this.selectedFontText
      };
      
      // Combine form properties with engraving properties for the ring
      const ringProperties = {
        ...properties,
        ...engravingProperties
      };
      
      const data = {
        items: [
          {
            id: variantId,
            quantity: formData.get('quantity') || 1,
            properties: ringProperties
          },
          {
            id: this.engravingProductId,
            quantity: 1,
            properties: {
              ...engravingProperties,
              '_ring_id': variantId
            }
          }
        ]
      };
      
      // Add both products to cart together
      fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })
      .then(response => response.json())
      .then(result => {
        // Redirect to cart if that's the cart action setting
        const addToCartButton = document.getElementById('purchaseComplete');
        if (addToCartButton && addToCartButton.dataset.cartAction === 'page') {
          window.location.href = window.routes.cart_url;
        } else {
          // Default to redirecting to cart
          window.location.href = window.routes.cart_url;
        }
      })
      .catch(error => {
        console.error('Error in cart process:', error);
        // If there's an error, try to submit the form normally
        event.target.submit();
      });
    }
    
    // Track where the mouse down event occurs
    handleMouseDown(event) {
      if (!this.hasAttribute('open')) return;
      
      // Check if the click is outside the modal content
      const isOutside = event.target === this;
      this.mouseDownOutside = isOutside;
    }
    
    // Only close the modal if both mousedown and mouseup happened outside
    handleMouseUp(event) {
      if (!this.hasAttribute('open')) return;
      
      // Check if mouseup is outside the modal content
      const isOutside = event.target === this;
      
      // Only close if both mousedown AND mouseup happened outside
      if (isOutside && this.mouseDownOutside) {
        this.hide();
      }
      
      // Reset the state for next interaction
      this.mouseDownOutside = false;
    }
    
    // Remove any existing event handlers
    removeHandlers() {
      const oldClickHandler = this._clickHandler;
      if (oldClickHandler) {
        this.removeEventListener('click', oldClickHandler);
        this._clickHandler = null;
      }
      
      const oldPointerHandler = this._pointerHandler;
      if (oldPointerHandler) {
        this.removeEventListener('pointerup', oldPointerHandler);
        this._pointerHandler = null;
      }
    }
    
    // Initialize the UI based on current state
    initializeUI() {
      // If engraving is active, update the UI
      if (this.engravingActive && this.engravingText) {
        this.updateEngravingStatus(true, this.selectedFontText, this.engravingText);
        
        // Set values in the form
        const textarea = this.querySelector('.engraving-text');
        if (textarea) {
          textarea.value = this.engravingText;
          // Update counter
          const counter = this.querySelector('.characters-counter');
          if (counter) counter.textContent = this.engravingText.length;
        }
        
        const fontSelect = this.querySelector('#engraving_font');
        if (fontSelect) {
          for (let i = 0; i < fontSelect.options.length; i++) {
            if (fontSelect.options[i].value === this.engravingFont) {
              fontSelect.selectedIndex = i;
              break;
            }
          }
        }
      }
    }
    
    // Update the button UI based on engraving status
    updateEngravingStatus(enabled, font = '', text = '') {
      const opener = document.querySelector('product-engraving-widget-opener');
      if (!opener) return;
      
      const button = opener.querySelector('#product-engraving-widget-opener');
      const statusText = opener.querySelector('.engraving-status-text');
      const details = opener.querySelector('.engraving-details');
      const fontEl = opener.querySelector('.engraving-font');
      const messageEl = opener.querySelector('.engraving-message');
      const iconPlus = opener.querySelector('.icon-plus');
      const iconEdit = opener.querySelector('.icon-edit');
      
      if (enabled && text) {
        // Show engraving details
        if (statusText) statusText.style.display = 'none';
        if (details) details.style.display = 'inline';
        if (fontEl) fontEl.textContent = font;
        if (messageEl) messageEl.textContent = text;
        
        // Show edit icon
        if (iconPlus) iconPlus.style.display = 'none';
        if (iconEdit) iconEdit.style.display = 'inline-block';
        
        // Apply the enabled style class
        if (button) button.classList.add('engraving-enabled');
      } else {
        // Show "Add" text
        if (statusText) statusText.style.display = 'inline';
        if (details) details.style.display = 'none';
        
        // Show plus icon
        if (iconPlus) iconPlus.style.display = 'inline-block';
        if (iconEdit) iconEdit.style.display = 'none';
        
        // Remove the enabled style class
        if (button) button.classList.remove('engraving-enabled');
      }
      
      // Update the opener's state
      if (opener.updateEngravingState) {
        opener.updateEngravingState(enabled, font, text);
      }
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      document.body.appendChild(this);
    }

    show(opener) {
      super.show(opener);
    }

    hide() {
      super.hide();
      // Any additional engraving-specific cleanup can go here if needed
    }
    
    disconnectedCallback() {
      // Clean up event listeners when element is removed
      document.removeEventListener('mousedown', this.handleMouseDown);
      document.removeEventListener('mouseup', this.handleMouseUp);
    }
    
    // Method to get current engraving properties for product form submission
    getEngravingProperties() {
      if (this.engravingActive && this.engravingText) {
        return {
          'Engraving text': this.engravingText,
          'Engraving font': this.selectedFontText
        };
      }
      return {};
    }

    // Update the apply button state based on text content and checkbox state
    updateApplyButtonState() {
      const textarea = this.querySelector('.engraving-text');
      const termsCheckbox = this.querySelector('#engraving_terms');
      const applyButton = this.querySelector('.apply');
      
      // Enable button if there is text AND the checkbox is checked
      const shouldEnable = textarea.value && termsCheckbox.checked;
      applyButton.disabled = !shouldEnable;
    }
  }
  customElements.define('product-engraving-modal', ProductEngravingModal);
}
if (!window.customElements.get('product-engraving-widget-opener')) {
  class ProductEngravingWidgetOpener extends HTMLElement {
    constructor() {
      super();
      this.engravingActive = false;
      this.engravingText = '';
      this.engravingFont = '';

      const button = this.querySelector('button');
      if (!button) return;
      button.addEventListener('click', () => {
        const modal = document.querySelector(this.getAttribute('data-modal'));
        if (modal) modal.show(button);
      });
    }
    
    // Method to be called from the modal to update the state
    updateEngravingState(active, font, text) {
      this.engravingActive = active;
      this.engravingFont = font;
      this.engravingText = text;
      this.updateDisplay(active, font, text);
    }
    
    updateDisplay(enabled, font = '', text = '') {
      const button = this.querySelector('#product-engraving-widget-opener');
      const statusText = this.querySelector('.engraving-status-text');
      const details = this.querySelector('.engraving-details');
      const fontEl = this.querySelector('.engraving-font');
      const messageEl = this.querySelector('.engraving-message');
      const iconPlus = this.querySelector('.icon-plus');
      const iconEdit = this.querySelector('.icon-edit');
      
      if (enabled && text) {
        // Show engraving details
        if (statusText) statusText.style.display = 'none';
        if (details) details.style.display = 'inline';
        if (fontEl) fontEl.textContent = font;
        if (messageEl) messageEl.textContent = text;
        
        // Show edit icon
        if (iconPlus) iconPlus.style.display = 'none';
        if (iconEdit) iconEdit.style.display = 'inline-block';
        
        // Apply the enabled style class
        if (button) button.classList.add('engraving-enabled');
      } else {
        // Show "Add" text
        if (statusText) statusText.style.display = 'inline';
        if (details) details.style.display = 'none';
        
        // Show plus icon
        if (iconPlus) iconPlus.style.display = 'inline-block';
        if (iconEdit) iconEdit.style.display = 'none';
        
        // Remove the enabled style class
        if (button) button.classList.remove('engraving-enabled');
      }
    }
    
    // Method to get current engraving properties for product form submission
    getEngravingProperties() {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal && typeof modal.getEngravingProperties === 'function') {
        return modal.getEngravingProperties();
      }
      return {};
    }
  }
  customElements.define('product-engraving-widget-opener', ProductEngravingWidgetOpener);
  
  // Add a listener to the product form to include engraving properties on submit
  document.addEventListener('DOMContentLoaded', () => {
    const productForms = document.querySelectorAll('form[action$="/cart/add"]');
    productForms.forEach(form => {
      form.addEventListener('submit', function(event) {
        const engravingOpener = document.querySelector('product-engraving-widget-opener');
        if (engravingOpener && typeof engravingOpener.getEngravingProperties === 'function') {
          const engravingProps = engravingOpener.getEngravingProperties();
          
          // Add engraving properties to the form
          for (const [key, value] of Object.entries(engravingProps)) {
            // Check if the property input already exists
            let input = form.querySelector(`input[name="properties[${key}]"]`);
            if (!input) {
              // Create the input if it doesn't exist
              input = document.createElement('input');
              input.type = 'hidden';
              input.name = `properties[${key}]`;
              form.appendChild(input);
            }
            input.value = value;
          }
        }
      });
    });
  });
  
  const trapFocusHandlers = {};
  function trapFocus(container, elementToFocus = container) {
    var elements = getFocusableElements(container);
    var first = elements[0];
    var last = elements[elements.length - 1];

    removeTrapFocus();

    trapFocusHandlers.focusin = (event) => {
      if (event.target !== container && event.target !== last && event.target !== first) return;

      document.addEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.focusout = function () {
      document.removeEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.keydown = function (event) {
      if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
      // On the last focusable element and tab forward, focus the first element.
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }

      //  On the first focusable element and tab backward, focus the last element.
      if ((event.target === container || event.target === first) && event.shiftKey) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener('focusout', trapFocusHandlers.focusout);
    document.addEventListener('focusin', trapFocusHandlers.focusin);

    if (!elementToFocus) {
      return;
    }

    elementToFocus.focus();

    if (elementToFocus.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(elementToFocus.type) && elementToFocus.value) {
      elementToFocus.setSelectionRange(0, elementToFocus.value.length);
    }
  }
  function removeTrapFocus(elementToFocus = null) {
    document.removeEventListener('focusin', trapFocusHandlers.focusin);
    document.removeEventListener('focusout', trapFocusHandlers.focusout);
    document.removeEventListener('keydown', trapFocusHandlers.keydown);

    if (elementToFocus) elementToFocus.focus();
  }
  function getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
      )
    );
  }
}